import React from 'react';

import classes from './styles.module.scss';

export interface PriceInfoProps {
    /** Promo / discount label shown in the coloured header (e.g. "50% off for 2 months"). */
    discountLabel?: string;
    /** Current (post-discount) price, formatted without a period suffix (e.g. "$1000"). */
    price: string;
    /** Billing period suffix shown next to the price (e.g. "/mo"). */
    period: string;
    /** Original price shown struck-through (e.g. "$2000/mo"). Omitted when there is no saving. */
    originalPrice?: string;
    /** Operator-written deal lines shown under the price, already formatted. */
    primaryLine?: string;
    secondaryLine?: string;
    /** Whether this card is the currently selected option. */
    active?: boolean;
    /** When true the card renders as a selectable button. */
    selectable?: boolean;
    /** Read-only summary variant: drops the selection radio and centres the label. */
    summary?: boolean;
    /** Drops the card border — used when the unit has no deal to frame. */
    borderless?: boolean;
    onSelect?: () => void;
}

/**
 * Selectable price / deal card used in the standard unit listing pricing column.
 * Mirrors the "Price Info" component from the SUL redesign: a coloured discount
 * header with a radio indicator, the current price with billing period and an
 * optional struck-through original price. The active state is emphasised in the
 * primary colour.
 */
export default function PriceInfo({
    discountLabel,
    price,
    period,
    originalPrice,
    primaryLine,
    secondaryLine,
    active = false,
    selectable = false,
    summary = false,
    borderless = false,
    onSelect,
}: PriceInfoProps) {
    const content = (
        <>
            {discountLabel ? (
                <span className={classes.discountHeader}>
                    {selectable && !summary ? (
                        <span className={classes.dealRadio} aria-hidden="true" />
                    ) : null}
                    <span className={classes.discountText}>{discountLabel}</span>
                </span>
            ) : null}
            <span className={classes.priceDetails}>
                <span className={classes.priceRow}>
                    <span className={classes.current}>{price}</span>
                    <span className={classes.period}>{period}</span>
                </span>
                {originalPrice ? (
                    <span className={classes.original}>{originalPrice}</span>
                ) : null}
                {primaryLine ? (
                    <span className={classes.customPrimary}>{primaryLine}</span>
                ) : null}
                {secondaryLine ? (
                    <span className={classes.customSecondary}>{secondaryLine}</span>
                ) : null}
            </span>
        </>
    );

    const className = `${classes.priceInfo} ${active ? classes.priceInfoActive : ''} ${
        !discountLabel ? classes.priceInfoPlain : ''
    } ${summary ? classes.priceInfoSummary : ''} ${
        borderless ? classes.priceInfoBorderless : ''
    }`;

    if (!selectable) {
        return <div className={className}>{content}</div>;
    }

    return (
        <button
            type="button"
            className={className}
            aria-pressed={active}
            onClick={onSelect}
        >
            {content}
        </button>
    );
}
